/**
 * ПРАВИЛА DRAG AND DROP аналитического дерева.
 *
 * По умолчанию любой элемент можно перетащить в любой другой.
 * Сверху накладываются запретительные ограничения:
 *
 * 1) ПАПКИ: можно тащить в любую другую папку.
 *    Исключение: нельзя тащить папку в себя или в своих потомков (любого уровня).
 *    Дочернюю папку можно тащить в корень. Папку нельзя тащить ни в какой другой тип.
 *
 * 2) ВЫБОРКИ: можно тащить в другую выборку (включая корневые буквально и по смыслу),
 *    но нельзя в своих потомков. Не буквально корневые можно тащить в корень.
 *    Буквально корневые (isRoot && parent_type == null) в корень НЕЛЬЗЯ.
 *
 * 3) ВЫБОРКИ В ПАПКИ: можно в любую папку. Но «корневую по смыслу»
 *    (isRoot === true ИЛИ parent_type === FOLDER) нельзя тащить в другую выборку и в список.
 *
 * 4) ПРОЕКЦИИ: вложенные в выборку (parent_type === SELECTION) — никуда нельзя.
 *    Корневые по смыслу (в папке) — можно только в папку. В корень — только из папки.
 *
 * 5) ГРАФИКИ: можно тащить в выборки, папки и списки. В корень НЕЛЬЗЯ.
 *
 * 6) СПИСКИ: можно тащить только в папку.
 *    Не буквально корневые (в папке) — в корень. В выборку, список и др. — нельзя.
 *
 * 7) В СПИСОК (как цель): можно тащить дочерние выборки (не корневые по смыслу)
 *    и графики. Корневые по смыслу элементы — нельзя.
 *    [Реализуется через canDropElement для SELECTION и CHART]
 *
 * ДОПОЛНИТЕЛЬНЫЕ ОГРАНИЧЕНИЯ:
 * - Нельзя тащить элемент в своего непосредственного родителя (лишний запрос).
 * - Выборку нельзя тащить внутрь проекции.
 * - Буквально корневые папки (isRoot && parent_type == null) нельзя тащить в корень (уже там).
 *
 * МАРШРУТИЗАЦИЯ DnD (определяется в BaseListElementLite.vue):
 * - CARD_ROOT: isRoot === true И elType !== FOLDER
 * - CARD_CHILD: все остальные (включая ВСЕ папки)
 * Папки всегда CARD_CHILD, root-to-root касается только выборок/списков.
 */

import { BaseItemTypes, DragItemTypes } from '@/components/dnd/ItemTypes';

export const CHILD_ARRAYS = ['FolderChildren', 'ChildSelections', 'Charts', 'ChildProjections', 'ChildReportElements'] as const;

const CHART_TYPES: BaseItemTypes[] = [BaseItemTypes.BAR_CHART, BaseItemTypes.PIE_CHART, BaseItemTypes.GRAPH];

// ---------------------------------------------------------------------------
//  Вспомогательные функции для обхода дерева
// ---------------------------------------------------------------------------

/** Найти папку в дереве по Id + FolderId. Обходит только FolderChildren. */
export function findFolderByIdentity(
  items: any[],
  id: number | undefined,
  folderId: number | undefined,
): any | null {
  for (const item of items) {
    if (item.elType === BaseItemTypes.FOLDER) {
      const sameId = (item.Id ?? null) === (id ?? null);
      const sameFolderId = (item.FolderId ?? null) === (folderId ?? null);
      if (sameId && sameFolderId) return item;
    }
    const children = item.FolderChildren as any[] | undefined;
    if (children) {
      const found = findFolderByIdentity(children, id, folderId);
      if (found) return found;
    }
  }
  return null;
}

/** Проверка: targetFolder является той же нодой, что dragFolder, или её потомком (по ссылке). */
export function isFolderSelfOrDescendant(dragFolder: any, targetFolder: any): boolean {
  if (dragFolder === targetFolder) return true;
  const children = dragFolder.FolderChildren as any[] | undefined;
  if (!children || children.length === 0) return false;
  for (const ch of children) {
    if (ch.elType !== BaseItemTypes.FOLDER) continue;
    if (ch === targetFolder) return true;
    if (isFolderSelfOrDescendant(ch, targetFolder)) return true;
  }
  return false;
}

/**
 * Полное сравнение Identity элемента (как при выборе): elType + Id + FolderId + SrcType + DestType.
 * node — элемент дерева (поля PascalCase: Id, FolderId, SrcType, DestType).
 * el — callbackParams (поля camelCase: id, folderId, sourceDataType, destinationDataType).
 */
export function sameElementIdentity(node: any | undefined, el: any | undefined): boolean {
  if (!node || !el) return false;
  if (node.elType !== el.elType) return false;
  const nodeId = node.Id ?? null;
  const nodeFolderId = node.FolderId ?? null;
  const elId = el.id ?? null;
  const elFolderId = el.folderId ?? null;
  const nodeSrc = node.SrcType ?? null;
  const nodeDest = node.DestType ?? null;
  const elSrc = el.sourceDataType ?? null;
  const elDest = el.destinationDataType ?? null;
  return nodeId === elId && nodeFolderId === elFolderId && nodeSrc === elSrc && nodeDest === elDest;
}

/** Рекурсивный поиск элемента в дереве по полной Identity. */
export function findElementByIdentity(items: any[], el: any): any | null {
  for (const item of items) {
    if (sameElementIdentity(item, el)) return item;
    for (const key of CHILD_ARRAYS) {
      const arr = item[key] as any[] | undefined;
      if (!arr || arr.length === 0) continue;
      const found = findElementByIdentity(arr, el);
      if (found) return found;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
//  Чистые правила (зависят только от свойств drag/hover элементов)
// ---------------------------------------------------------------------------

/**
 * Матрица разрешений по типам элементов.
 * По умолчанию разрешено всё; каждое правило может только ЗАПРЕТИТЬ.
 * dragEl / hoverEl — callbackParams с полями id, folderId, elType, isRoot, parent_type.
 */
export function canDropElement(dragEl: any, hoverEl: any): boolean {
  if (!dragEl || !hoverEl) return false;
  if (dragEl.id != null && hoverEl.id != null && dragEl.id === hoverEl.id && dragEl.elType === hoverEl.elType) return false;

  const drag = dragEl.elType as BaseItemTypes;
  const target = hoverEl.elType as BaseItemTypes;

  // ОТЧЁТЫ. Сегмент REPORT работает в режиме sibling-reorder:
  //  - REPORT (top-level) можно тащить только на другой REPORT (между корневыми отчётами).
  //  - Ребёнок отчёта (parent_type === REPORT) — только на ребёнка ТОГО ЖЕ отчёта.
  //  - Никакой кросс-перенос между обычным деревом и деревом отчёта не разрешён.
  const dragInReport = drag === BaseItemTypes.REPORT || dragEl.parent_type === BaseItemTypes.REPORT;
  const hoverInReport = target === BaseItemTypes.REPORT || hoverEl.parent_type === BaseItemTypes.REPORT;
  if (dragInReport || hoverInReport) {
    if (!(dragInReport && hoverInReport)) return false;
    if (drag === BaseItemTypes.REPORT) return target === BaseItemTypes.REPORT;
    if (target === BaseItemTypes.REPORT) return false;
    // Дети отчёта: разрешаем только в пределах одного и того же родителя-отчёта.
    return dragEl.parent_id != null
      && hoverEl.parent_id != null
      && dragEl.parent_id === hoverEl.parent_id
      && dragEl.parent_type === BaseItemTypes.REPORT
      && hoverEl.parent_type === BaseItemTypes.REPORT;
  }

  // «Корневая по смыслу» выборка: буквально isRoot или лежит в папке.
  // Используется в правилах 3 и 7.
  const isRootByMeaning = dragEl.isRoot === true || dragEl.parent_type === BaseItemTypes.FOLDER;

  // Правило 1: папка -> только папка
  if (drag === BaseItemTypes.FOLDER) return target === BaseItemTypes.FOLDER;

  // Правило 4: проекции
  if (drag === BaseItemTypes.PROJECTION) {
    if (dragEl.parent_type === BaseItemTypes.SELECTION) return false;
    return target === BaseItemTypes.FOLDER;
  }

  // Правила 2, 3, 7: выборки
  if (drag === BaseItemTypes.SELECTION) {
    if (target === BaseItemTypes.PROJECTION) return false;
    if (isRootByMeaning && (target === BaseItemTypes.SELECTION || target === BaseItemTypes.LIST)) return false;
    return true;
  }

  // Правило 6: списки -> только папка
  if (drag === BaseItemTypes.LIST) return target === BaseItemTypes.FOLDER;

  // Правило 5: графики -> выборки, папки, списки
  if (CHART_TYPES.includes(drag)) {
    return target === BaseItemTypes.SELECTION || target === BaseItemTypes.FOLDER || target === BaseItemTypes.LIST;
  }

  return true;
}

/**
 * Разрешено ли тащить элемент в корневые полосы (слева/справа — зона «в корень»).
 * dragEl — callbackParams.
 */
export function canDropToRoot(dragEl: any): boolean {
  if (!dragEl) return false;
  const dragType = dragEl.elType as BaseItemTypes;
  const parentType = dragEl.parent_type as BaseItemTypes | undefined;

  // Узлы отчётного дерева (сам REPORT и его дети) на корневые полосы не сбрасываются:
  // в сегменте REPORT поддерживается только sibling-reorder в пределах одного родителя-отчёта.
  if (dragType === BaseItemTypes.REPORT || parentType === BaseItemTypes.REPORT) {
    return false;
  }

  // Папки: буквально корневая (isRoot && parent_type == null) уже в корне → нельзя.
  // Дочерние папки — можно.
  if (dragType === BaseItemTypes.FOLDER) {
    const isLiteralRootFolder = dragEl.isRoot === true && parentType == null;
    return !isLiteralRootFolder;
  }

  // Выборки: буквально корневая в корень НЕЛЬЗЯ, остальные МОЖНО.
  if (dragType === BaseItemTypes.SELECTION) {
    const isLiteralRoot = dragEl.isRoot === true && parentType == null;
    return !isLiteralRoot;
  }

  // Списки и проекции: в корень только из папки (корневые по смыслу).
  if (dragType === BaseItemTypes.LIST || dragType === BaseItemTypes.PROJECTION) {
    return parentType === BaseItemTypes.FOLDER;
  }

  // Графики и прочее: в корень нельзя.
  return false;
}

// ---------------------------------------------------------------------------
//  Правила, зависящие от дерева (tree передаётся как аргумент)
// ---------------------------------------------------------------------------

/**
 * Запрет: папка в себя или в свою вложенную (потомка). Возвращает true если запрещено.
 * Для не-папок всегда возвращает false. Для CARD_ROOT → CARD_ROOT тоже false
 * (на корневом уровне потомков быть не может).
 */
export function isFolderDropForbidden(dragParams: any, hoverParams: any, tree: any[]): boolean {
  const dragElType = dragParams?.callbackParams?.elType ?? dragParams?.elType;
  if (dragElType !== BaseItemTypes.FOLDER) return false;

  const hoverType = hoverParams?.type;
  if (hoverType === DragItemTypes.CARD_ROOT) return false;

  const dragId = dragParams?.callbackParams?.id ?? dragParams?.id;
  const dragFolderId = dragParams?.callbackParams?.folderId ?? dragParams?.folderId;
  const hoverId = hoverParams?.callbackParams?.id ?? hoverParams?.id;
  const hoverFolderId = hoverParams?.callbackParams?.folderId ?? hoverParams?.folderId;

  const dragFolder = findFolderByIdentity(tree, dragId, dragFolderId);
  const hoverFolder = findFolderByIdentity(tree, hoverId, hoverFolderId);
  if (!dragFolder || !hoverFolder) return false;

  return isFolderSelfOrDescendant(dragFolder, hoverFolder);
}

/**
 * Правило 1 для папок: любую папку можно в другую папку, кроме себя и потомков.
 * Для не-папок всегда true (не ограничивает).
 */
export function folderDropAllowed(dragParams: any, hoverParams: any, tree: any[]): boolean {
  const dragEl = dragParams.callbackParams ?? dragParams;
  const hoverEl = hoverParams.callbackParams ?? hoverParams;
  if (!dragEl || !hoverEl) return false;
  if (dragEl.elType !== BaseItemTypes.FOLDER || hoverEl.elType !== BaseItemTypes.FOLDER) return true;
  return !isFolderDropForbidden(dragParams, hoverParams, tree);
}

/**
 * Проверка: hoverEl является непосредственным родителем dragEl (один уровень вверх).
 * Если да — дроп бессмыслен (элемент уже там лежит), вернёт true.
 */
export function isImmediateParentByIdentity(tree: any[], dragEl: any, hoverEl: any): boolean {
  if (!dragEl || !hoverEl) return false;
  const parentNode = findElementByIdentity(tree, hoverEl);
  if (!parentNode) return false;
  for (const key of CHILD_ARRAYS) {
    const arr = parentNode[key] as any[] | undefined;
    if (!arr || arr.length === 0) continue;
    for (const child of arr) {
      if (sameElementIdentity(child, dragEl)) return true;
    }
  }
  return false;
}

// ---------------------------------------------------------------------------
//  Маршрутизаторы по типу DnD-карточки (CARD_ROOT / CARD_CHILD)
// ---------------------------------------------------------------------------

/** CARD_CHILD → CARD_CHILD */
export function allowChildToChildDropAction(dragParams: any, hoverParams: any, tree: any[]): boolean {
  if (dragParams.type !== DragItemTypes.CARD_CHILD || hoverParams.type !== DragItemTypes.CARD_CHILD) return false;
  const dragEl = dragParams.callbackParams ?? dragParams;
  const hoverEl = hoverParams.callbackParams ?? hoverParams;
  if (isImmediateParentByIdentity(tree, dragEl, hoverEl)) return false;
  if (dragEl?.elType === BaseItemTypes.FOLDER && hoverEl?.elType === BaseItemTypes.FOLDER) {
    return folderDropAllowed(dragParams, hoverParams, tree);
  }
  return canDropElement(dragEl, hoverEl);
}

/** CARD_CHILD → CARD_ROOT */
export function allowChildToRootDropAction(dragParams: any, hoverParams: any, tree: any[]): boolean {
  if (dragParams.type !== DragItemTypes.CARD_CHILD || hoverParams.type !== DragItemTypes.CARD_ROOT) return false;
  const dragEl = dragParams.callbackParams ?? dragParams;
  const hoverEl = hoverParams.callbackParams ?? hoverParams;
  if (isImmediateParentByIdentity(tree, dragEl, hoverEl)) return false;
  if (dragEl?.elType === BaseItemTypes.FOLDER && hoverEl?.elType === BaseItemTypes.FOLDER) {
    return folderDropAllowed(dragParams, hoverParams, tree);
  }
  return canDropElement(dragEl, hoverEl);
}

/** CARD_ROOT → CARD_ROOT */
export function allowRootToRootDropAction(dragParams: any, hoverParams: any, _tree: any[]): boolean {
  if (dragParams.type !== DragItemTypes.CARD_ROOT || hoverParams.type !== DragItemTypes.CARD_ROOT) return false;
  const dragEl = dragParams.callbackParams ?? dragParams;
  const hoverEl = hoverParams.callbackParams ?? hoverParams;
  if (dragEl?.id != null && hoverEl?.id != null && dragEl.id === hoverEl.id && dragEl?.elType === hoverEl?.elType) return false;
  // Папка→папка на корне: между корневыми потомков быть не может, достаточно запрета «в себя».
  if (dragEl?.elType === BaseItemTypes.FOLDER && hoverEl?.elType === BaseItemTypes.FOLDER) {
    return true;
  }
  return canDropElement(dragEl, hoverEl);
}

/** CARD_ROOT → CARD_CHILD: корневой элемент на дочерний. Цель — только папка. */
export function allowRootToChildDropAction(dragParams: any, hoverParams: any, tree: any[]): boolean {
  if (dragParams.type !== DragItemTypes.CARD_ROOT || hoverParams.type !== DragItemTypes.CARD_CHILD) return false;
  const dragEl = dragParams.callbackParams ?? dragParams;
  const hoverEl = hoverParams.callbackParams ?? hoverParams;
  if (hoverEl?.elType !== BaseItemTypes.FOLDER) return false;
  if (dragEl?.elType === BaseItemTypes.FOLDER) {
    return folderDropAllowed(dragParams, hoverParams, tree);
  }
  return canDropElement(dragEl, hoverEl);
}

/** Объединённая проверка для подсветки при наведении. */
export function allowHighlightOnHover(dragParams: any, hoverParams: any, tree: any[]): boolean {
  return allowChildToRootDropAction(dragParams, hoverParams, tree) ||
    allowChildToChildDropAction(dragParams, hoverParams, tree) ||
    allowRootToRootDropAction(dragParams, hoverParams, tree) ||
    allowRootToChildDropAction(dragParams, hoverParams, tree);
}

/**
 * Определяет тип DnD-карточки по свойствам элемента.
 * Папки ВСЕГДА CARD_CHILD (даже корневые). Корневые не-папки — CARD_ROOT.
 */
export function getDragCardType(isRoot: boolean, elType: BaseItemTypes): DragItemTypes {
  return isRoot && elType !== BaseItemTypes.FOLDER ? DragItemTypes.CARD_ROOT : DragItemTypes.CARD_CHILD;
}
